import {
  AsYouType,
  type CountryCode,
  isPossiblePhoneNumber,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import type { Market } from '../../../domain/firmographics/Firmographics';
import { splitExtension } from '../../../domain/phone/nanpPhone';

/**
 * A bare national number masked as (555) 555-0100, as it is typed. More than ten digits starting with 1 carry the
 * country code without a plus, and the 1 is dropped, or it would become the area code and push the last digit out.
 * Ten or fewer are the number itself, as while an area code is edited: deleting the 2 of 212 leaves a 1 in front,
 * which is one of its digits.
 */
function maskNanp(typed: string): string {
  const all = typed.replace(/\D/g, '');
  const national = all.length > 10 && all.startsWith('1') ? all.slice(1) : all;
  const digits = national.slice(0, 10);
  if (digits.length === 0) {
    return '';
  }
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * A phone number as it is typed. In a NANP market it is masked as (555) 555-0100, keeping a +1 in front, as the
 * extractor writes one: read as digits, the 1 would become the area code and the last digit would be lost. A number
 * with any other country code, and any number in another market, is left as entered.
 */
export function formatPhone(
  typed: string,
  format: Market['phoneFormat']
): string {
  if (format !== 'NANP') {
    return typed;
  }
  if (!typed.trimStart().startsWith('+')) {
    return maskNanp(typed);
  }
  const national = /^\s*\+1[\s.-]*(.*)$/.exec(typed);
  if (national === null) {
    return typed;
  }
  return `+1 ${maskNanp(national[1])}`.trimEnd();
}

/** Eve's example number, shown in an empty phone field in a market whose numbers are masked; none in any other. */
export function phonePlaceholder(
  format: Market['phoneFormat']
): string | undefined {
  return format === 'NANP' ? '(XXX) XXX-XXXX' : undefined;
}

/** How to write a number from another country than the market's: the hint under every phone field. */
export const PHONE_HINT =
  'For a number in another country, start with + and its country code.';

/** A number with too few or too many digits for where it is. The hint under the field says how to write one abroad. */
export const PHONE_INCOMPLETE = 'Enter the full phone number.';

/** The phone numbering plan of a market's country. The market says UK; the numbering plans call it GB. */
const PLAN_OF: Readonly<Record<string, CountryCode>> = { UK: 'GB' };

/** The numbering plan a market's country is read in. */
export const planOf = (country: string): string => PLAN_OF[country] ?? country;

/** Countries whose numbers are masked as (555) 555-0100, as the quote flow's NANP markets are. */
export const NANP_COUNTRIES: ReadonlySet<string> = new Set(['US', 'CA']);

/**
 * A phone number as it is typed, in the format of the country it is read in: masked as (555) 555-0100 in the US and
 * Canada, and in the numbering plan's own national format elsewhere (020 7946 0018 in the UK). A number with a + code
 * is formatted for the country that code names, in any country. A national number where the country is unknown is left
 * as entered.
 */
export function formatPhoneFor(typed: string, country: string): string {
  const trimmed = typed.trimStart();
  const nanpHere = NANP_COUNTRIES.has(country);
  if (nanpHere && (!trimmed.startsWith('+') || trimmed.startsWith('+1'))) {
    return formatPhone(typed, 'NANP');
  }
  if (!trimmed) {
    return typed;
  }
  const plan = planOf(country);
  if (trimmed.startsWith('+')) {
    return new AsYouType().input(trimmed);
  }
  return isSupportedCountry(plan)
    ? new AsYouType(plan as CountryCode).input(trimmed)
    : typed;
}

/**
 * Why a phone number can't be taken, or null when it can or is empty (whether one is required is the form's to say).
 * A number is read against the market's own country, or against the country its + code names, so a client or a
 * contact abroad is taken in any market. It must have as many digits as a number there has: that catches a number cut
 * short or run on, without refusing one in a range the numbering plans list doesn't know yet.
 */
export function phoneError(value: string, country: string): string | null {
  if (!value.trim()) {
    return null;
  }
  const plan = planOf(country);
  const known = isSupportedCountry(plan);
  // A national number in a country the numbering plans don't know: nothing to check it against.
  if (!known && !value.trim().startsWith('+')) {
    return null;
  }
  return isPossiblePhoneNumber(value, known ? (plan as CountryCode) : undefined)
    ? null
    : PHONE_INCOMPLETE;
}

/** A number with the extension typed after it, split from it. */
export { splitExtension };

/** Ten digits, or eleven with the 1 in front: a number written without a +, in the shape of a NANP number. */
const NANP_SHAPE = /^1?\d{10}$/;

/**
 * A phone number as it is read, not typed: its country code, then the number the way that country writes it. +1 (316)
 * 258-4026 in the US and Canada, +44 20 7946 0018 in the UK. A number with a + code is read by the country that code
 * names. A national number is read in `country` (any spelling: US, UK or GB), or as a NANP number when the country is
 * unknown and it has the shape of one. A number that cannot be read as one is returned as written, since a wrong code
 * put on it would be worse than none. An extension is kept after it as "ext. 123".
 */
export function formatPhoneDisplay(
  phone: string | null | undefined,
  country?: string | null
): string {
  const written = phone?.trim() ?? '';
  if (!written) {
    return '';
  }
  const { number, extension } = splitExtension(written);
  const plan = country ? planOf(country.trim().toUpperCase()) : undefined;
  const known = plan && isSupportedCountry(plan) ? (plan as CountryCode) : null;
  const digits = number.replace(/\D/g, '');
  const fallback = NANP_SHAPE.test(digits) ? 'US' : undefined;
  const parsed = parsePhoneNumberFromString(
    number,
    number.trimStart().startsWith('+') ? undefined : known ?? fallback
  );
  if (!parsed?.isPossible()) {
    return written;
  }
  const formatted =
    parsed.countryCallingCode === '1'
      ? `+1 ${parsed.formatNational()}`
      : parsed.formatInternational();
  return extension ? `${formatted} ext. ${extension}` : formatted;
}
